import { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, apiPost } from '../../../lib/api';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Contest {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  contestType: string;
  type?: string;
  startDate?: string;
  endDate: string;
  status: string;
  config: Record<string, unknown>;
  entryConfig: Record<string, unknown>;
  winValidation?: string;
  rulesUrl?: string | null;
}

interface Round {
  id: string;
  contestId: string;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
}

interface Entitlement {
  playsEarned: number;
  playsUsed: number;
  playsRemaining: number;
}

interface PlayResult {
  outcome: 'win' | 'lose' | 'consolation';
  prize?: { id: string; prizeType: string; prizeValue: Record<string, unknown> };
  consolationValue?: { points: number };
  attemptId: string;
}

type GameType = 'scratch_card' | 'spin_wheel' | 'slot_machine' | 'gift_box';

interface ContestPlayModalProps {
  contest: Contest;
  onClose: () => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatPrize(prize: PlayResult['prize']): string {
  if (!prize) return '';
  const v = prize.prizeValue;
  if (prize.prizeType === 'points' && typeof v.points === 'number')
    return `${(v.points as number).toLocaleString()} Points`;
  if (prize.prizeType === 'coupon' && v.code) return `Coupon: ${v.code}`;
  if (prize.prizeType === 'voucher' && typeof v.amount === 'number')
    return `€${v.amount} Voucher`;
  return 'A Prize';
}

function computeRoundStatus(round: Round): string {
  const now = new Date();
  const start = new Date(round.startDate);
  const end = new Date(round.endDate);
  if (now < start) return 'upcoming';
  if (now > end) return 'completed';
  return 'active';
}

// ─── Game: Scratch Card ─────────────────────────────────────────────────────

function ScratchCardGame({
  onPlay,
  playing,
}: {
  onPlay: () => void;
  playing: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scratching, setScratching] = useState(false);
  const [scratchPercent, setScratchPercent] = useState(0);
  const isDrawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Draw the scratch surface
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#667eea');
    gradient.addColorStop(1, '#764ba2');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add sparkle pattern
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    for (let i = 0; i < 30; i++) {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      ctx.beginPath();
      ctx.arc(x, y, Math.random() * 3 + 1, 0, Math.PI * 2);
      ctx.fill();
    }

    // Scratch text
    ctx.font = 'bold 18px system-ui';
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.textAlign = 'center';
    ctx.fillText('SCRATCH HERE', canvas.width / 2, canvas.height / 2 - 8);
    ctx.font = '13px system-ui';
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillText('Use your mouse to reveal', canvas.width / 2, canvas.height / 2 + 16);
  }, []);

  const scratch = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (!isDrawing.current) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const rect = canvas.getBoundingClientRect();
      let clientX: number, clientY: number;
      if ('touches' in e) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      const x = clientX - rect.left;
      const y = clientY - rect.top;

      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(x, y, 25, 0, Math.PI * 2);
      ctx.fill();

      // Calculate scratch percentage
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let transparent = 0;
      for (let i = 3; i < imageData.data.length; i += 4) {
        if (imageData.data[i] === 0) transparent++;
      }
      const pct = (transparent / (imageData.data.length / 4)) * 100;
      setScratchPercent(pct);

      if (pct > 40 && !scratching) {
        setScratching(true);
        onPlay();
      }
    },
    [onPlay, scratching],
  );

  return (
    <div className="relative w-full max-w-sm mx-auto">
      <div
        className="relative rounded-2xl overflow-hidden shadow-lg"
        style={{ aspectRatio: '16/10' }}
      >
        {/* Hidden layer (question mark pattern) */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center">
          <div className="text-6xl animate-pulse opacity-30">❓</div>
          {scratchPercent > 40 && !playing && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-3 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
          )}
        </div>
        {/* Scratch canvas */}
        <canvas
          ref={canvasRef}
          width={400}
          height={250}
          className="absolute inset-0 w-full h-full cursor-pointer touch-none"
          onMouseDown={() => (isDrawing.current = true)}
          onMouseUp={() => (isDrawing.current = false)}
          onMouseLeave={() => (isDrawing.current = false)}
          onMouseMove={scratch}
          onTouchStart={() => (isDrawing.current = true)}
          onTouchEnd={() => (isDrawing.current = false)}
          onTouchMove={scratch}
        />
      </div>
      {scratchPercent < 5 && (
        <p className="text-center text-xs text-[var(--color-text-muted)] mt-3 animate-bounce">
          👆 Scratch the card to play!
        </p>
      )}
    </div>
  );
}

// ─── Game: Spin Wheel ───────────────────────────────────────────────────────

function SpinWheelGame({
  onPlay,
  playing,
}: {
  onPlay: () => void;
  playing: boolean;
}) {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);

  const segments = [
    { label: '🎁', color: '#FF6B6B' },
    { label: '⭐', color: '#4ECDC4' },
    { label: '🎉', color: '#FFE66D' },
    { label: '💎', color: '#A78BFA' },
    { label: '🏆', color: '#F97316' },
    { label: '🌟', color: '#06B6D4' },
    { label: '🎯', color: '#EC4899' },
    { label: '✨', color: '#10B981' },
  ];

  const handleSpin = () => {
    if (spinning || playing) return;
    setSpinning(true);
    const newRotation = rotation + 1440 + Math.random() * 360;
    setRotation(newRotation);
    onPlay();
  };

  return (
    <div className="flex flex-col items-center">
      {/* Pointer */}
      <div className="relative z-10 -mb-2">
        <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-red-500 drop-shadow-md" />
      </div>

      {/* Wheel */}
      <div className="relative w-64 h-64">
        <svg
          viewBox="0 0 200 200"
          className="w-full h-full drop-shadow-xl"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: spinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : undefined,
          }}
          onTransitionEnd={() => setSpinning(false)}
        >
          {segments.map((seg, i) => {
            const angle = 360 / segments.length;
            const startAngle = ((i * angle - 90) * Math.PI) / 180;
            const endAngle = (((i + 1) * angle - 90) * Math.PI) / 180;
            const x1 = 100 + 95 * Math.cos(startAngle);
            const y1 = 100 + 95 * Math.sin(startAngle);
            const x2 = 100 + 95 * Math.cos(endAngle);
            const y2 = 100 + 95 * Math.sin(endAngle);
            const midAngle = ((i * angle + angle / 2 - 90) * Math.PI) / 180;
            const tx = 100 + 60 * Math.cos(midAngle);
            const ty = 100 + 60 * Math.sin(midAngle);

            return (
              <g key={i}>
                <path
                  d={`M100,100 L${x1},${y1} A95,95 0 0,1 ${x2},${y2} Z`}
                  fill={seg.color}
                  stroke="white"
                  strokeWidth="1"
                />
                <text
                  x={tx}
                  y={ty}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="20"
                >
                  {seg.label}
                </text>
              </g>
            );
          })}
          {/* Center circle */}
          <circle cx="100" cy="100" r="15" fill="white" stroke="#e5e7eb" strokeWidth="2" />
          <circle cx="100" cy="100" r="8" fill="#6366f1" />
        </svg>
      </div>

      {/* Spin button */}
      <button
        onClick={handleSpin}
        disabled={spinning || playing}
        className="mt-4 px-8 py-3 rounded-full font-bold text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 shadow-lg"
      >
        {spinning ? '🎡 Spinning...' : playing ? 'Wait...' : '🎡 SPIN!'}
      </button>
    </div>
  );
}

// ─── Game: Slot Machine ─────────────────────────────────────────────────────

function SlotMachineGame({
  onPlay,
  playing,
}: {
  onPlay: () => void;
  playing: boolean;
}) {
  const symbols = ['🍒', '🍋', '🍊', '⭐', '💎', '7️⃣', '🔔'];
  const [reels, setReels] = useState(['❓', '❓', '❓']);
  const [spinning, setSpinning] = useState(false);
  const [animClasses, setAnimClasses] = useState(['', '', '']);

  const handlePull = () => {
    if (spinning || playing) return;
    setSpinning(true);
    setAnimClasses(['animate-slot-spin', 'animate-slot-spin', 'animate-slot-spin']);

    // Show random symbols while spinning
    const interval = setInterval(() => {
      setReels([
        symbols[Math.floor(Math.random() * symbols.length)],
        symbols[Math.floor(Math.random() * symbols.length)],
        symbols[Math.floor(Math.random() * symbols.length)],
      ]);
    }, 100);

    onPlay();

    // Stop reels one by one
    setTimeout(() => {
      setAnimClasses((prev) => [prev[0], prev[1], '']);
    }, 800);
    setTimeout(() => {
      setAnimClasses((prev) => [prev[0], '', '']);
    }, 1200);
    setTimeout(() => {
      setAnimClasses(['', '', '']);
      clearInterval(interval);
      setSpinning(false);
    }, 1600);
  };

  return (
    <div className="flex flex-col items-center">
      {/* Machine body */}
      <div className="bg-gradient-to-b from-zinc-800 to-zinc-900 rounded-2xl p-6 shadow-2xl border border-zinc-700">
        {/* Title bar */}
        <div className="text-center mb-4">
          <div className="text-xs font-bold text-yellow-400 tracking-[0.3em] uppercase">
            Jackpot
          </div>
          <div className="flex justify-center gap-1 mt-1">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full bg-yellow-400"
                style={{ animation: `pulse 1s ease-in-out ${i * 0.2}s infinite` }}
              />
            ))}
          </div>
        </div>

        {/* Reels */}
        <div className="flex gap-2 bg-white/10 rounded-xl p-3">
          {reels.map((symbol, i) => (
            <div
              key={i}
              className={`w-20 h-20 bg-white rounded-lg flex items-center justify-center text-4xl shadow-inner ${animClasses[i]}`}
              style={{
                transition: 'transform 0.3s',
                transform: spinning ? 'scale(0.95)' : 'scale(1)',
              }}
            >
              {symbol}
            </div>
          ))}
        </div>

        {/* Pull lever button */}
        <button
          onClick={handlePull}
          disabled={spinning || playing}
          className="w-full mt-4 py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 shadow-lg"
        >
          {spinning ? '🎰 Spinning...' : playing ? 'Wait...' : '🎰 PULL LEVER!'}
        </button>
      </div>
    </div>
  );
}

// ─── Game: Gift Box ─────────────────────────────────────────────────────────

function GiftBoxGame({
  onPlay,
  playing,
}: {
  onPlay: () => void;
  playing: boolean;
}) {
  const [opened, setOpened] = useState(false);
  const [shaking, setShaking] = useState(false);

  const handleOpen = () => {
    if (opened || playing) return;
    setShaking(true);

    // Shake for 1 second then open
    setTimeout(() => {
      setShaking(false);
      setOpened(true);
      onPlay();
    }, 1000);
  };

  return (
    <div className="flex flex-col items-center">
      <div
        onClick={handleOpen}
        className={`relative cursor-pointer select-none transition-transform duration-300 ${
          shaking ? 'animate-wiggle' : ''
        } ${opened ? 'scale-110' : 'hover:scale-105'}`}
      >
        {!opened ? (
          // Closed gift box
          <div className="relative w-40 h-44">
            {/* Box body */}
            <div className="absolute bottom-0 w-40 h-32 bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-xl">
              {/* Vertical ribbon */}
              <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-6 bg-yellow-400" />
              {/* Horizontal ribbon */}
              <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-6 bg-yellow-400" />
            </div>
            {/* Lid */}
            <div className="absolute top-0 w-44 -left-2 h-14 bg-gradient-to-br from-red-400 to-red-500 rounded-lg shadow-lg border-b-2 border-red-700">
              {/* Bow */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-4xl">🎀</div>
            </div>
          </div>
        ) : (
          // Opened gift box
          <div className="relative w-40 h-44">
            {/* Box body */}
            <div className="absolute bottom-0 w-40 h-32 bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-xl">
              <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-6 bg-yellow-400" />
            </div>
            {/* Lid - flipped open */}
            <div
              className="absolute -top-8 w-44 -left-2 h-14 bg-gradient-to-br from-red-400 to-red-500 rounded-lg shadow-lg"
              style={{
                transform: 'perspective(300px) rotateX(-70deg)',
                transformOrigin: 'bottom center',
              }}
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-4xl">🎀</div>
            </div>
            {/* Sparkles coming out */}
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-3xl animate-bounce">
              ✨
            </div>
          </div>
        )}
      </div>

      {!opened && !playing && (
        <button
          onClick={handleOpen}
          disabled={shaking}
          className="mt-6 px-8 py-3 rounded-full font-bold text-white bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 disabled:opacity-50 transition-all active:scale-95 shadow-lg"
        >
          {shaking ? '📦 Opening...' : '🎁 OPEN GIFT!'}
        </button>
      )}
    </div>
  );
}

// ─── Game Selector ──────────────────────────────────────────────────────────

function GameSelector({
  selected,
  onSelect,
}: {
  selected: GameType;
  onSelect: (g: GameType) => void;
}) {
  const games: { type: GameType; emoji: string; label: string }[] = [
    { type: 'scratch_card', emoji: '🎴', label: 'Scratch' },
    { type: 'spin_wheel', emoji: '🎡', label: 'Spin' },
    { type: 'slot_machine', emoji: '🎰', label: 'Slots' },
    { type: 'gift_box', emoji: '🎁', label: 'Gift' },
  ];

  return (
    <div className="flex gap-2 justify-center">
      {games.map((g) => (
        <button
          key={g.type}
          onClick={() => onSelect(g.type)}
          className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
            selected === g.type
              ? 'bg-[var(--color-primary)] text-white shadow-md scale-105'
              : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-border)]'
          }`}
        >
          <span className="text-lg">{g.emoji}</span>
          {g.label}
        </button>
      ))}
    </div>
  );
}

// ─── Result Overlay ─────────────────────────────────────────────────────────

function ResultOverlay({
  result,
  onDismiss,
  onPlayAgain,
  canPlayAgain,
}: {
  result: PlayResult;
  onDismiss: () => void;
  onPlayAgain: () => void;
  canPlayAgain: boolean;
}) {
  return (
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-20 animate-fadeIn rounded-2xl">
      <div className="bg-white rounded-2xl p-8 mx-4 max-w-sm w-full text-center shadow-2xl animate-scaleIn">
        {result.outcome === 'win' ? (
          <>
            <div className="text-6xl mb-3 animate-bounce">🏆</div>
            <h3 className="text-2xl font-bold text-green-600 mb-2">YOU WON!</h3>
            {result.prize && (
              <p className="text-green-700 font-semibold text-lg mb-1">
                {formatPrize(result.prize)}
              </p>
            )}
            <p className="text-zinc-500 text-sm">Congratulations!</p>
          </>
        ) : result.outcome === 'consolation' ? (
          <>
            <div className="text-6xl mb-3">⭐</div>
            <h3 className="text-2xl font-bold text-amber-600 mb-2">Consolation Prize!</h3>
            {result.consolationValue && (
              <p className="text-amber-700 font-semibold text-lg mb-1">
                +{result.consolationValue.points.toLocaleString()} Points
              </p>
            )}
            <p className="text-zinc-500 text-sm">Not bad! Keep trying for the big prize.</p>
          </>
        ) : (
          <>
            <div className="text-6xl mb-3">😔</div>
            <h3 className="text-2xl font-bold text-zinc-700 mb-2">Not this time!</h3>
            <p className="text-zinc-500 text-sm">Better luck on your next play.</p>
          </>
        )}

        <div className="flex gap-3 mt-6">
          <button
            onClick={onDismiss}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-zinc-200 text-zinc-600 hover:bg-zinc-50 transition-colors"
          >
            Close
          </button>
          {canPlayAgain && (
            <button
              onClick={onPlayAgain}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity"
            >
              Play Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Modal ─────────────────────────────────────────────────────────────

export function ContestPlayModal({ contest, onClose }: ContestPlayModalProps) {
  const [rounds, setRounds] = useState<Round[]>([]);
  const [activeRound, setActiveRound] = useState<Round | null>(null);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [gameType, setGameType] = useState<GameType>('scratch_card');
  const [playing, setPlaying] = useState(false);
  const [result, setResult] = useState<PlayResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingState, setLoadingState] = useState<'rounds' | 'ready' | 'error'>('rounds');
  const [playHistory, setPlayHistory] = useState<PlayResult[]>([]);

  const cType = contest.contestType ?? contest.type ?? 'instant_win';

  // Derive game type from contest config if available
  useEffect(() => {
    const configGameUI = contest.config?.gameUI as string | undefined;
    if (configGameUI === 'wheel' || configGameUI === 'spin_wheel') setGameType('spin_wheel');
    else if (configGameUI === 'scratch' || configGameUI === 'scratch_card') setGameType('scratch_card');
    else if (configGameUI === 'slots' || configGameUI === 'slot_machine') setGameType('slot_machine');
    else if (configGameUI === 'gift' || configGameUI === 'gift_box') setGameType('gift_box');
  }, [contest.config]);

  // Fetch rounds + find active one
  useEffect(() => {
    let cancelled = false;
    setLoadingState('rounds');
    setError(null);

    apiGet<Round[]>(`/customer/loyalty/contests/${contest.id}/rounds`)
      .then((data) => {
        if (cancelled) return;
        // Enrich rounds: prefer DB status, fall back to computed from dates
        const roundsWithStatus = data.map((r) => ({
          ...r,
          status: r.status === 'active' || r.status === 'completed' || r.status === 'upcoming'
            ? r.status
            : computeRoundStatus(r),
        }));
        setRounds(roundsWithStatus);

        // Find active round: first try DB status, then computed status
        let active = roundsWithStatus.find((r) => r.status === 'active');
        if (!active) {
          // Fallback: compute status from dates
          active = roundsWithStatus.find((r) => computeRoundStatus(r) === 'active');
        }
        if (active) {
          setActiveRound(active);
          setLoadingState('ready');
        } else {
          setLoadingState('error');
          setError('No active round found for this contest.');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadingState('error');
          setError('Could not load contest rounds.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [contest.id]);

  // Fetch entitlement when round is set
  const fetchEntitlement = useCallback(() => {
    if (!activeRound) return;
    apiGet<Entitlement>(
      `/customer/loyalty/contests/${contest.id}/entitlement?roundId=${activeRound.id}`,
    )
      .then(setEntitlement)
      .catch(() => setEntitlement({ playsEarned: 0, playsUsed: 0, playsRemaining: 0 }));
  }, [contest.id, activeRound]);

  useEffect(() => {
    fetchEntitlement();
  }, [fetchEntitlement]);

  const isUnlimited = entitlement?.playsRemaining === -1;

  // Play handler
  const handlePlay = async () => {
    if (playing || !activeRound) return;
    if (!isUnlimited && entitlement && entitlement.playsRemaining <= 0) {
      setError('No plays remaining!');
      return;
    }
    setPlaying(true);
    setResult(null);
    setError(null);

    try {
      const res = await apiPost<PlayResult>(
        `/customer/loyalty/contests/${contest.id}/play`,
        {
          roundId: activeRound.id,
          gameType,
        },
      );
      setResult(res);
      setPlayHistory((prev) => [res, ...prev]);
      if (!isUnlimited) fetchEntitlement();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('No plays') || msg.includes('422')) {
        setError('No plays remaining. Earn more plays to continue!');
      } else if (msg.includes('not active') || msg.includes('409')) {
        setError('This contest or round is not currently active.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setPlaying(false);
    }
  };

  const handlePlayAgain = () => {
    setResult(null);
  };

  const playsLeft = isUnlimited ? Infinity : (entitlement?.playsRemaining ?? 0);

  // Close on escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scaleIn">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 rounded-t-2xl border-b border-zinc-100">
          <div className="flex items-center justify-between p-4">
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-lg text-zinc-900 truncate">{contest.title}</h2>
              {activeRound && (
                <p className="text-xs text-zinc-500 mt-0.5">{activeRound.name}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="ml-3 w-8 h-8 flex items-center justify-center rounded-full text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Plays counter (hidden for unlimited contests) */}
          {entitlement && loadingState === 'ready' && !isUnlimited && (
            <div className="px-4 pb-3 flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-[var(--color-primary)]">{playsLeft}</span>
                <span className="text-xs text-zinc-500">
                  play{playsLeft !== 1 ? 's' : ''} remaining
                </span>
              </div>
              <span className="ml-auto text-xs text-zinc-400">
                {entitlement.playsUsed}/{entitlement.playsEarned} used
              </span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-6">
          {loadingState === 'rounds' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-10 h-10 border-3 border-zinc-200 border-t-zinc-600 rounded-full animate-spin mb-4" />
              <p className="text-sm text-zinc-500">Loading contest...</p>
            </div>
          )}

          {loadingState === 'error' && (
            <div className="text-center py-12">
              <div className="text-4xl mb-3">😕</div>
              <p className="text-zinc-600 font-semibold mb-2">Cannot Play</p>
              <p className="text-sm text-zinc-500">{error}</p>
              <button
                onClick={onClose}
                className="mt-4 px-6 py-2 rounded-lg text-sm font-semibold bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors"
              >
                Go Back
              </button>
            </div>
          )}

          {loadingState === 'ready' && cType === 'instant_win' && (
            <div className="space-y-6">
              {/* Game type selector */}
              <GameSelector selected={gameType} onSelect={setGameType} />

              {/* Game area */}
              <div className="relative min-h-[280px] flex items-center justify-center">
                {!isUnlimited && playsLeft <= 0 && !result ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-3">🔒</div>
                    <p className="font-semibold text-zinc-700 mb-1">No Plays Available</p>
                    <p className="text-sm text-zinc-500">
                      Earn more plays to continue playing!
                    </p>
                  </div>
                ) : (
                  <>
                    {gameType === 'scratch_card' && (
                      <ScratchCardGame onPlay={handlePlay} playing={playing} />
                    )}
                    {gameType === 'spin_wheel' && (
                      <SpinWheelGame onPlay={handlePlay} playing={playing} />
                    )}
                    {gameType === 'slot_machine' && (
                      <SlotMachineGame onPlay={handlePlay} playing={playing} />
                    )}
                    {gameType === 'gift_box' && (
                      <GiftBoxGame onPlay={handlePlay} playing={playing} />
                    )}
                  </>
                )}

                {/* Result overlay */}
                {result && (
                  <ResultOverlay
                    result={result}
                    onDismiss={onClose}
                    onPlayAgain={handlePlayAgain}
                    canPlayAgain={isUnlimited || playsLeft > 0}
                  />
                )}
              </div>

              {/* Error */}
              {error && !result && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm text-center">
                  {error}
                </div>
              )}
            </div>
          )}

          {loadingState === 'ready' && cType === 'draw' && (
            <DrawPlayView contest={contest} roundId={activeRound!.id} onClose={onClose} />
          )}

          {loadingState === 'ready' && cType === 'leaderboard' && (
            <LeaderboardPlayView contest={contest} roundId={activeRound!.id} />
          )}

          {/* Play history */}
          {playHistory.length > 0 && (
            <div className="mt-6 pt-4 border-t border-zinc-100">
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                Recent Plays
              </h4>
              <div className="space-y-1.5">
                {playHistory.slice(0, 5).map((play, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-sm py-1 px-2 rounded-lg bg-zinc-50"
                  >
                    <span>
                      {play.outcome === 'win'
                        ? '🏆'
                        : play.outcome === 'consolation'
                        ? '⭐'
                        : '😔'}
                    </span>
                    <span
                      className={`font-semibold ${
                        play.outcome === 'win'
                          ? 'text-green-600'
                          : play.outcome === 'consolation'
                          ? 'text-amber-600'
                          : 'text-zinc-500'
                      }`}
                    >
                      {play.outcome === 'win'
                        ? `Won ${formatPrize(play.prize) || 'a prize'}`
                        : play.outcome === 'consolation'
                        ? `+${play.consolationValue?.points ?? 0} pts`
                        : 'No win'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          10% { transform: rotate(-8deg); }
          20% { transform: rotate(8deg); }
          30% { transform: rotate(-6deg); }
          40% { transform: rotate(6deg); }
          50% { transform: rotate(-4deg); }
          60% { transform: rotate(4deg); }
          70% { transform: rotate(-2deg); }
          80% { transform: rotate(2deg); }
          90% { transform: rotate(-1deg); }
        }
        @keyframes slot-spin {
          0% { transform: translateY(0); }
          25% { transform: translateY(-8px); }
          50% { transform: translateY(0); }
          75% { transform: translateY(8px); }
          100% { transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.3s ease-out; }
        .animate-wiggle { animation: wiggle 1s ease-in-out; }
        .animate-slot-spin { animation: slot-spin 0.15s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

// ─── Draw Play View (inline in modal) ───────────────────────────────────────

function DrawPlayView({
  contest,
  roundId,
  onClose,
}: {
  contest: Contest;
  roundId: string;
  onClose: () => void;
}) {
  const [entered, setEntered] = useState(false);
  const [checking, setChecking] = useState(true);
  const [entering, setEntering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ entered: boolean }>(
      `/customer/loyalty/contests/${contest.id}/entered?roundId=${roundId}`,
    )
      .then((res) => setEntered(res.entered))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [contest.id, roundId]);

  const handleEnter = async () => {
    if (entering || entered) return;
    setEntering(true);
    setError(null);
    try {
      await apiPost(`/customer/loyalty/contests/${contest.id}/enter`, { roundId });
      setEntered(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('already') || msg.includes('409')) {
        setEntered(true);
      } else {
        setError('Could not enter the draw. Please try again.');
      }
    } finally {
      setEntering(false);
    }
  };

  if (checking) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-3 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="text-center py-6">
      {entered ? (
        <>
          <div className="text-6xl mb-4 animate-bounce">🎟️</div>
          <h3 className="text-xl font-bold text-green-600 mb-2">You're Entered!</h3>
          <p className="text-zinc-500 text-sm mb-6">
            Good luck! Winners will be announced when the round ends.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl font-semibold text-sm bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors"
          >
            Done
          </button>
        </>
      ) : (
        <>
          <div className="text-6xl mb-4">🎟️</div>
          <h3 className="text-xl font-bold text-zinc-800 mb-2">Enter the Draw</h3>
          <p className="text-zinc-500 text-sm mb-6">
            {contest.description ?? 'Join this draw for a chance to win prizes!'}
          </p>
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}
          <button
            onClick={handleEnter}
            disabled={entering}
            className="px-8 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition-all active:scale-95 shadow-lg"
          >
            {entering ? 'Entering...' : 'Enter Draw'}
          </button>
        </>
      )}
    </div>
  );
}

// ─── Leaderboard Play View (inline in modal) ────────────────────────────────

function LeaderboardPlayView({
  contest,
  roundId,
}: {
  contest: Contest;
  roundId: string;
}) {
  interface LeaderboardEntry {
    rank: number;
    customerId: string;
    score: number;
    firstName?: string;
    lastName?: string;
  }
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet<{ entries: LeaderboardEntry[] }>(
      `/customer/loyalty/leaderboard/${roundId}?limit=10`,
    )
      .then((res) => setEntries(res.entries ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [roundId]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-3 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="text-center mb-6">
        <div className="text-4xl mb-2">🏆</div>
        <h3 className="text-lg font-bold text-zinc-800">{contest.title}</h3>
        <p className="text-sm text-zinc-500">Current standings</p>
      </div>

      {entries.length === 0 ? (
        <p className="text-center text-sm text-zinc-400 py-6">No entries yet.</p>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => {
            const medal =
              entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : null;
            return (
              <div
                key={entry.customerId}
                className="flex items-center gap-3 py-2 px-3 rounded-xl bg-zinc-50"
              >
                <span className="w-8 text-center font-bold text-zinc-400">
                  {medal ?? `#${entry.rank}`}
                </span>
                <span className="flex-1 text-sm font-medium text-zinc-700">
                  {entry.firstName && entry.lastName
                    ? `${entry.firstName} ${entry.lastName[0]}.`
                    : `Player ${entry.rank}`}
                </span>
                <span className="font-bold text-zinc-900">{entry.score.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
